// libs.
import { useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

// styles.
import { WrapperModal } from "./styles";

// components.
import { DeleteBonus } from "./DeleteBonus";
import { BonusInfoItem } from "@store/apiSlices/bonus/interfaces/interfaces";
import { BonusPercentageInput } from "./BonusPercentageInput";

// utils.
import { formatPrice } from "@utils";
import { calculateBonusAmount, toDisplayPercentage } from "./utils";

interface BonosModalProps {
  bonusInfo?: BonusInfoItem;
  country: "CO" | "MX";
  existsBonus?: boolean;
  isLoading: boolean;
  minPublicationPrice: number;
  onClose: () => void;
  onConfirm: (
    comment: string,
    percentage: number,
    amount: number,
    bonusType: string,
  ) => void;
  onReset?: () => void;
  publicationPrice: number;
  show: boolean;
  title: string;
}

type FormValues = {
  bonusType: string;
  comment: string;
  percentage: string;
};

export const BonusModal = ({
  existsBonus,
  bonusInfo,
  show,
  onClose,
  onConfirm,
  onReset,
  title,
  country,
  isLoading,
  minPublicationPrice,
  publicationPrice,
}: BonosModalProps) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      bonusType: "",
      comment: "",
      percentage: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (show) {
      reset({
        bonusType: bonusInfo?.bonus_type ?? "",
        comment: bonusInfo?.meta.comentario ?? "",
        percentage: existsBonus
          ? toDisplayPercentage(bonusInfo?.percentage ?? 0).toString()
          : "",
      });
    }
  }, [show, bonusInfo, reset]);

  const handleFormSubmit = (data: FormValues) => {
    const amount = calculateBonusAmount({
      country,
      minPrice: minPublicationPrice,
      percentage: Number(data.percentage),
      publicationPrice: publicationPrice,
    });
    onConfirm(
      data.comment.trim(),
      Number(data.percentage),
      amount,
      data.bonusType.trim(),
    );
  };

  const handleClose = () => {
    reset();
    onReset?.();
    onClose();
  };

  const formattedAmount = formatPrice(
    calculateBonusAmount({
      country,
      minPrice: minPublicationPrice,
      percentage: Number(watch("percentage")) ?? 0,
      publicationPrice: publicationPrice,
    }),
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <WrapperModal>
        <Modal.Header className="d-flex px-1 py-2 border-bottom-0 justify-content-between">
          <Modal.Title>{title}</Modal.Title>
          {existsBonus && bonusInfo && (
            <DeleteBonus bonusInfo={bonusInfo} onClose={onClose} />
          )}
        </Modal.Header>
        <FormProvider {...methods}>
          <Form>
            <Modal.Body className="px-1 p-0 pb-3">
              <Form.Group className="d-flex flex-column gap-2">
                <BonusPercentageInput
                  country={country}
                  errors={errors}
                  register={register}
                />
                <div className="percentage-amount d-flex justify-content-between align-items-center gap-2">
                  <strong>Total bono:</strong> {formattedAmount}
                </div>
                <div className="d-flex flex-column">
                  <Form.Label>Tipo de bono (opcional)</Form.Label>
                  <Form.Control
                    type="text"
                    className="bonus-type-input"
                    placeholder="Ej: promo"
                    {...register("bonusType")}
                  />
                </div>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Deja un comentario..."
                  {...register("comment", {
                    required: "Este campo es obligatorio",
                    validate: (value) =>
                      value.trim() !== "" || "Este campo es obligatorio",
                  })}
                  isInvalid={!!errors.comment}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.comment?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="p-0 pb-1 border-top-0 flex-nowrap">
              <Button
                className="fw-bold btn-secundary-habi rounded-2 py-2 px-4 w-100"
                onClick={handleClose}
              >
                Cerrar
              </Button>
              <Button
                disabled={isLoading}
                className="btn-habi rounded-2 fw-bold py-2 px-4 w-100"
                type="button"
                onClick={() => handleSubmit(handleFormSubmit)()}
              >
                {isLoading ? "Guardando…" : "Guardar"}
              </Button>
            </Modal.Footer>
          </Form>
        </FormProvider>
      </WrapperModal>
    </Modal>
  );
};
